import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export interface ExpressGiftData {
  recipientName: string;
  occasion: string;
  customOccasion?: string;
  relationship?: string;
  currentSituation?: string;
  specialDetails?: string;
}

interface ExpressGiftFormProps {
  data: ExpressGiftData;
  onChange: (data: ExpressGiftData) => void;
}

const OCCASIONS = [
  { value: 'birthday', label: 'Birthday' },
  { value: 'graduation', label: 'Graduation' },
  { value: 'just-because', label: 'Just Because' },
  { value: 'stressful-time', label: 'Stressful Time' },
  { value: 'get-well', label: 'Get Well' },
  { value: 'thank-you', label: 'Thank You' },
  { value: 'encouragement', label: 'Encouragement' },
  { value: 'new-job', label: 'New Job' },
  { value: 'breakup', label: 'Breakup / Heartbreak' },
  { value: 'loss', label: 'Loss / Grief' },
  { value: 'other', label: 'Other' },
];

const RELATIONSHIPS = [
  { value: 'friend', label: 'Friend' },
  { value: 'parent', label: 'Parent' },
  { value: 'child', label: 'Child' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'partner', label: 'Partner / Spouse' },
  { value: 'colleague', label: 'Colleague' },
  { value: 'mentor', label: 'Mentor' },
  { value: 'client', label: 'Client' },
  { value: 'other', label: 'Other' },
];

const ExpressGiftForm: React.FC<ExpressGiftFormProps> = ({ data, onChange }) => {
  const updateField = <K extends keyof ExpressGiftData>(field: K, value: ExpressGiftData[K]) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-5">
      {/* Recipient Name */}
      <div className="space-y-2">
        <Label htmlFor="recipientName" className="text-base font-medium">
          Who is this gift for? *
        </Label>
        <Input
          id="recipientName"
          placeholder="Their name (e.g., Mom, Sarah, David)"
          value={data.recipientName}
          onChange={(e) => updateField('recipientName', e.target.value)}
          className="text-base"
        />
      </div>

      {/* Occasion */}
      <div className="space-y-2">
        <Label htmlFor="occasion" className="text-base font-medium">
          What's the occasion? *
        </Label>
        <Select value={data.occasion} onValueChange={(value) => updateField('occasion', value)}>
          <SelectTrigger className="w-full bg-background text-foreground border-border">
            <SelectValue placeholder="Select an occasion" />
          </SelectTrigger>
          <SelectContent className="bg-popover text-popover-foreground z-[200]">
            {OCCASIONS.map((occasion) => (
              <SelectItem key={occasion.value} value={occasion.value} className="hover:bg-accent">
                {occasion.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data.occasion === 'other' && (
          <Input
            placeholder="Describe the occasion"
            value={data.customOccasion || ''}
            onChange={(e) => updateField('customOccasion', e.target.value)}
            className="mt-2"
          />
        )}
      </div>

      {/* Relationship */}
      <div className="space-y-2">
        <Label htmlFor="relationship" className="text-base font-medium">
          Your relationship to them
        </Label>
        <Select value={data.relationship || ''} onValueChange={(value) => updateField('relationship', value)}>
          <SelectTrigger className="w-full bg-background text-foreground border-border">
            <SelectValue placeholder="Select relationship (optional)" />
          </SelectTrigger>
          <SelectContent className="bg-popover text-popover-foreground z-[200]">
            {RELATIONSHIPS.map((rel) => (
              <SelectItem key={rel.value} value={rel.value} className="hover:bg-accent">
                {rel.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Current Situation */}
      <div className="space-y-2">
        <Label htmlFor="currentSituation" className="text-base font-medium">
          What are they going through?
        </Label>
        <Textarea
          id="currentSituation"
          placeholder="e.g., Just started a demanding new job, dealing with a lot of stress at home, recovering from surgery..."
          value={data.currentSituation || ''}
          onChange={(e) => updateField('currentSituation', e.target.value.slice(0, 1000))}
          rows={4}
          className="resize-none"
        />
        <p className="text-xs text-muted-foreground text-right">
          {(data.currentSituation || '').length}/1000
        </p>
      </div>

      {/* Special Details */}
      <div className="space-y-2">
        <Label htmlFor="specialDetails" className="text-base font-medium">
          Anything special to mention?
        </Label>
        <Textarea
          id="specialDetails"
          placeholder="e.g., Loves hiking and nature, has two kids, recently moved to Seattle, passionate about painting..."
          value={data.specialDetails || ''}
          onChange={(e) => updateField('specialDetails', e.target.value.slice(0, 1000))}
          rows={4}
          className="resize-none"
        />
        <p className="text-xs text-muted-foreground text-right">
          {(data.specialDetails || '').length}/1000
        </p>
      </div>
    </div>
  );
};

export default ExpressGiftForm;
